/** Conexión bancaria (Enable Banking), parte pura. Port fiel de Domain_Bank.js. */
import type { ImportRow } from './import.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
export function mapBankTransactions(transactions: any[]): { rows: ImportRow[]; pending: number; invalid: number } {
  const rows: ImportRow[] = [];
  let pending = 0, invalid = 0;
  (transactions ?? []).forEach((t, i) => {
    const status = String(t.status || 'BOOK').toUpperCase();
    if (status !== 'BOOK') { pending++; return; }
    const amt = Number((t.transaction_amount || {}).amount);
    const sign = String(t.credit_debit_indicator || '').toUpperCase() === 'DBIT' ? -1 : 1;
    const date = String(t.booking_date || t.transaction_date || t.value_date || '').slice(0, 10);
    const valueDate = String(t.value_date || date).slice(0, 10);
    // Sin IBAN (dato personal y ruido para comparar con el fichero)
    const info = (Array.isArray(t.remittance_information) ? t.remittance_information.join(' ') : String(t.remittance_information || ''))
      .replace(/\b[A-Z]{2}\d{2}(?:[A-Z0-9]{11,30}|(?: [A-Z0-9]{4}\b){2,7}(?: [A-Z0-9]{1,3}\b)?)\b/g, ' ').replace(/\s+/g, ' ').trim();
    const party = ((sign < 0 ? t.creditor : t.debtor) || {}).name || '';
    const description = [info, party].map(s => String(s || '').trim()).filter(Boolean).filter((s, j, a) => a.indexOf(s) === j).join(' - ')
      || String((t.bank_transaction_code && t.bank_transaction_code.description) || '').trim();
    const bal = t.balance_after_transaction && t.balance_after_transaction.balance_amount && Number(t.balance_after_transaction.balance_amount.amount);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(amt) || !description) { invalid++; return; }
    rows.push({
      valueDate: /^\d{4}-\d{2}-\d{2}$/.test(valueDate) ? valueDate : date, date, description,
      amount: Math.round(sign * Math.abs(amt) * 100) / 100,
      balanceAfter: Number.isFinite(bal) ? Math.round(bal * 100) / 100 : null,
      sourceRef: String(t.entry_reference || ''), fileIndex: i,
    });
  });
  return { rows, pending, invalid };
}

export const BANK_BALANCE_PREFERENCE = ['ITBD', 'CLBD', 'XPCD', 'ITAV', 'CLAV', 'OPBD', 'OPAV', 'ITAV', 'VALU'];

export function pickBankBalance(balances: any[]): { type: string; amount: number; date: string } | null {
  const list = (balances ?? []).map(b => ({ type: String(b.balance_type || '').toUpperCase(), amount: Number((b.balance_amount || {}).amount),
    date: String(b.reference_date || b.last_change_date_time || '').slice(0, 10) })).filter(b => Number.isFinite(b.amount));
  for (const type of BANK_BALANCE_PREFERENCE) { const b = list.find(x => x.type === type); if (b) return b; }
  return list[0] ?? null;
}

/** Fecha desde la que pedir movimientos: última sincronización − margen, sin ir antes del inicio. */
export function bankSyncFrom(lastSyncIso: string, todayIso: string, firstSyncDays: number, overlapDays: number, minIso?: string): string {
  const d = new Date(`${lastSyncIso || todayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (lastSyncIso ? overlapDays : firstSyncDays));
  const from = d.toISOString().slice(0, 10);
  return minIso && from < minIso ? minIso : from;
}

export function normBankName(s: unknown): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Banco de la lista de Enable Banking por nombre exacto (sin acentos ni mayúsculas), solo particulares. */
export function resolveAspsp(list: any[], cfg: { names: string[]; country: string }) {
  const cands = (list ?? []).filter(a => String(a.country || '') === cfg.country && (!a.psu_types || !a.psu_types.length || a.psu_types.includes('personal')));
  const wanted = cfg.names.map(normBankName);
  const pick = cands.find(a => wanted.includes(normBankName(a.name)));
  if (!pick) return null;
  const secs = Number(pick.maximum_consent_validity) || 0;
  return { name: String(pick.name), country: String(pick.country), maxConsentDays: secs ? Math.floor(secs / 86400) : null, beta: !!pick.beta };
}

/** Bancos que se pueden conectar, por formato de extracto (nombres tal como los lista Enable Banking). */
export const BANKS: Record<string, { label: string; names: string[]; country: string }> = {
  BBVA: { label: 'BBVA', names: ['BBVA'], country: 'ES' },
  ING: { label: 'ING', names: ['ING', 'ING España', 'ING Direct'], country: 'ES' },
  LK: { label: 'Laboral Kutxa', names: ['Laboral Kutxa', 'Caja Laboral'], country: 'ES' },
  // En el catálogo es «MyInvestor Banco»; «Banco Inversis» es otra entrada (otras claves): no se usa
  MYINVESTOR: { label: 'MyInvestor', names: ['MyInvestor Banco', 'MyInvestor'], country: 'ES' },
};

/** Consentimiento (días), primera sincronización (días hacia atrás), solape y límite PSD2 de accesos diarios. */
export const BANK_SYNC = { consentDays: 179, firstSyncDays: 90, overlapDays: 10, maxPerDay: 4 };
